import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ScanFace, ShieldCheck, ShieldX, UserCheck, RotateCcw, Save, Search, Loader2 } from "lucide-react";
import FaceCapture from "@/components/FaceCapture";

const OVERRIDE_LABEL = { global: "Use Global", enabled: "Force Enabled", disabled: "Force Disabled" };
const ENR_META = {
  enrolled: { label: "Enrolled", cls: "text-emerald-700 border-emerald-300 bg-emerald-50" },
  not_enrolled: { label: "Not Enrolled", cls: "text-slate-500 border-slate-300 bg-slate-50" },
  revoked: { label: "Revoked", cls: "text-rose-700 border-rose-300 bg-rose-50" },
};

export default function FaceVerificationPanel() {
  const [data, setData] = useState({ settings: {}, employees: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusF, setStatusF] = useState("active");
  const [sel, setSel] = useState(new Set());
  const [enrollFor, setEnrollFor] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState({ enabled: false, enforcement: "observe", match_threshold: 0.5, min_samples: 2 });

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ search, status: statusF }).toString();
    api.get(`/hr/attendance/face/management?${params}`).then((r) => {
      setData(r.data); setSettingsDraft(r.data.settings); setSel(new Set());
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/hr/attendance/face/settings", settingsDraft);
      toast.success("Face settings saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingSettings(false); }
  };

  const toggleAll = (rows) => {
    if (sel.size === rows.length) setSel(new Set());
    else setSel(new Set(rows.map((r) => r.employee_id)));
  };
  const toggleOne = (eid) => { const n = new Set(sel); n.has(eid) ? n.delete(eid) : n.add(eid); setSel(n); };

  const bulkOverride = async (mode) => {
    if (sel.size === 0) return toast.error("Select at least one employee");
    try {
      const r = await api.post("/hr/attendance/face/overrides", { employee_ids: [...sel], mode });
      toast.success(`Updated ${r.data.updated} employee(s) → ${OVERRIDE_LABEL[mode]}`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const revoke = async (eid) => {
    try { await api.post(`/hr/attendance/face/${eid}/revoke`); toast.success(`Face enrollment revoked for ${eid}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onEnrolled = async (descriptors) => {
    const ctx = enrollFor; setEnrollFor(null);
    if (!ctx) return;
    try {
      const r = await api.post("/hr/attendance/face/enroll", { employee_id: ctx.employee_id, descriptors });
      toast.success(`Enrolled ${ctx.employee_id} (${r.data.samples} samples)`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rows = data.employees || [];
  const enabledNotEnrolled = rows.filter((r) => r.effective && r.enrollment_status !== "enrolled");

  return (
    <div data-testid="face-verification-panel" className="space-y-5">
      {/* Global settings */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-3">
          <ScanFace className="w-5 h-5 text-sky-600" />
          <h3 className="font-semibold text-sm">Global Face Verification</h3>
        </div>
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-xs text-slate-500 block mb-1">Status</label>
            <button data-testid="face-global-toggle" onClick={() => setSettingsDraft({ ...settingsDraft, enabled: !settingsDraft.enabled })}
              className={`w-full h-10 rounded-md border text-sm font-medium ${settingsDraft.enabled ? "bg-emerald-600 text-white border-emerald-600" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
              {settingsDraft.enabled ? "Enabled" : "Disabled"}
            </button>
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Enforcement Mode</label>
            <select data-testid="face-enforcement-select" value={settingsDraft.enforcement}
              onChange={(e) => setSettingsDraft({ ...settingsDraft, enforcement: e.target.value })}
              className="w-full h-10 rounded-md border border-slate-200 text-sm px-2">
              <option value="observe">Observe / Test (never blocks)</option>
              <option value="required">Required (must pass)</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Match Threshold ({settingsDraft.match_threshold})</label>
            <input type="range" min="0.3" max="0.7" step="0.05" data-testid="face-threshold"
              value={settingsDraft.match_threshold}
              onChange={(e) => setSettingsDraft({ ...settingsDraft, match_threshold: parseFloat(e.target.value) })}
              className="w-full h-10" />
          </div>
          <Button onClick={saveSettings} disabled={savingSettings} data-testid="face-save-settings" className="h-10 bg-slate-900 hover:bg-slate-800">
            {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Save</>}
          </Button>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">
          Lower threshold = stricter match. Observe mode records results without blocking attendance — use it to pilot safely.
          Liveness/anti-spoof is not enabled this phase (basic 1-to-1 match only).
        </p>
      </div>

      {enabledNotEnrolled.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="face-not-enrolled-warning">
          <b>{enabledNotEnrolled.length}</b> employee(s) have Face Verification effective but are <b>not enrolled</b> yet: {enabledNotEnrolled.slice(0, 8).map((r) => r.employee_id).join(", ")}{enabledNotEnrolled.length > 8 ? "…" : ""}
        </div>
      )}

      {/* Filters + bulk */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input data-testid="face-search" value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search ID / name…" className="pl-8 h-9" />
        </div>
        <select data-testid="face-status-filter" value={statusF} onChange={(e) => setStatusF(e.target.value)}
          className="h-9 rounded-md border border-slate-200 text-sm px-2">
          <option value="active">Active</option>
          <option value="resigned">Resigned</option>
          <option value="">All</option>
        </select>
        <Button variant="outline" size="sm" onClick={load} className="h-9">Apply</Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-500">{sel.size} selected</span>
        <Button size="sm" variant="outline" data-testid="face-bulk-global" onClick={() => bulkOverride("global")}>Use Global</Button>
        <Button size="sm" variant="outline" data-testid="face-bulk-enable" onClick={() => bulkOverride("enabled")} className="text-emerald-700 border-emerald-300"><ShieldCheck className="w-3.5 h-3.5 mr-1" />Force Enabled</Button>
        <Button size="sm" variant="outline" data-testid="face-bulk-disable" onClick={() => bulkOverride("disabled")} className="text-rose-700 border-rose-300"><ShieldX className="w-3.5 h-3.5 mr-1" />Force Disabled</Button>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr>
              <th className="p-2 w-8"><input type="checkbox" data-testid="face-select-all" checked={rows.length > 0 && sel.size === rows.length} onChange={() => toggleAll(rows)} /></th>
              <th className="p-2 text-left">Employee</th>
              <th className="p-2 text-left">Dept</th>
              <th className="p-2 text-left">Override</th>
              <th className="p-2 text-left">Effective</th>
              <th className="p-2 text-left">Enrollment</th>
              <th className="p-2 text-left">Last Verified</th>
              <th className="p-2 text-left">Fails</th>
              <th className="p-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="p-8 text-center text-slate-400">No employees</td></tr>
            ) : rows.map((r) => {
              const em = ENR_META[r.enrollment_status] || ENR_META.not_enrolled;
              return (
                <tr key={r.employee_id} data-testid={`face-row-${r.employee_id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-2"><input type="checkbox" data-testid={`face-check-${r.employee_id}`} checked={sel.has(r.employee_id)} onChange={() => toggleOne(r.employee_id)} /></td>
                  <td className="p-2"><span className="font-mono text-xs">{r.employee_id}</span><div className="text-slate-600">{r.full_name}</div></td>
                  <td className="p-2 text-slate-500 text-xs">{r.department_name || "—"}</td>
                  <td className="p-2"><Badge variant="outline" className="text-xs">{OVERRIDE_LABEL[r.override]}</Badge></td>
                  <td className="p-2">{r.effective ? <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50 text-xs">On</Badge> : <Badge variant="outline" className="text-slate-500 text-xs">Off</Badge>}</td>
                  <td className="p-2"><Badge variant="outline" className={`text-xs ${em.cls}`}>{em.label}</Badge></td>
                  <td className="p-2 text-xs font-mono text-slate-500">{r.last_verified_at ? r.last_verified_at.slice(0, 16).replace("T", " ") : "—"}</td>
                  <td className="p-2 text-xs">{r.recent_failures > 0 ? <span className="text-rose-600 font-semibold">{r.recent_failures}</span> : "0"}</td>
                  <td className="p-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`face-enroll-${r.employee_id}`}
                      onClick={() => setEnrollFor({ employee_id: r.employee_id, full_name: r.full_name })}>
                      <UserCheck className="w-3.5 h-3.5 mr-1" />{r.enrollment_status === "enrolled" ? "Re-enroll" : "Enroll"}
                    </Button>
                    {r.enrollment_status === "enrolled" && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600" data-testid={`face-revoke-${r.employee_id}`} onClick={() => revoke(r.employee_id)}>
                        <RotateCcw className="w-3.5 h-3.5 mr-1" />Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <FaceCapture open={!!enrollFor} mode="enroll" samplesNeeded={3}
        title="Enroll Employee Face"
        subject={enrollFor ? `${enrollFor.employee_id} · ${enrollFor.full_name || ""}` : ""}
        onEnroll={onEnrolled} onClose={() => setEnrollFor(null)} />
    </div>
  );
}
